<template>
  <nav class="snav" aria-label="Site sections">
    <div class="snav-inner">
      <button class="snav-burger" @click="menuOpen = !menuOpen" :aria-expanded="String(menuOpen)" aria-label="Menu">
        <span class="snav-burger-ico">{{ menuOpen ? '✕' : '☰' }}</span>
      </button>

      <div class="snav-tabs" :class="{ open: menuOpen }">
        <div v-for="t in tabs" :key="t.key" class="snav-item" :class="[{ 'has-children': t.children }, 'snav-item-' + t.key]">
          <a :href="t.href" class="snav-tab" :class="['snav-' + t.key, { active: t.key === activeKey }]"
             :aria-current="t.key === activeKey ? 'page' : null" @click="menuOpen = false">
            <span class="snav-emoji" aria-hidden="true">{{ t.emoji }}</span><span class="snav-label">{{ t.label }}</span>
            <span v-if="t.children" class="snav-caret" aria-hidden="true">▾</span>
          </a>

          <!-- Players / Champions: a mega-menu of related pages & in-page modes -->
          <div v-if="t.children" class="snav-mega">
            <div class="snav-mega-head"><span class="snav-mega-title">{{ t.emoji }} {{ t.label }}</span><span class="snav-mega-sub">{{ t.desc }}</span></div>
            <div class="snav-mega-grid">
              <a v-for="c in t.children" :key="c.href" :href="c.href" class="snav-sub" :class="{ active: childActive(c.href) }" @click="menuOpen = false">
                <span class="snav-sub-label">{{ c.label }}</span>
                <span class="snav-sub-desc">{{ c.desc }}</span>
              </a>
            </div>
          </div>

          <!-- other tabs: a verbose styled hover tooltip -->
          <span v-else-if="t.desc" class="snav-tip">{{ t.desc }}</span>
        </div>
      </div>

      <div class="snav-actions"><slot name="actions" /></div>
    </div>
  </nav>
</template>

<script>
import { SECTIONS, resolveSection } from '../routes.js';
import { QUEST_CATALOG } from '../lib/questCatalog.js';
// Shared top navigation across every page. Each view renders <SiteNav active="…"/>
// at the top; the homepage also fills the #actions slot with its bracket controls
// (settings / help / generator / prev-tournament). Self-detects the active tab from
// the URL params when `active` isn't passed. The Players tab opens a mega-menu of
// its in-page modes (deep-linked via ?players&tab=…); the other tabs get a verbose
// styled hover tooltip. On phones the whole tab row collapses behind a ☰ hamburger.
// The menus were regrouped by what each page is for on 2026-10-23 (no page listed
// twice): 📜 History (the championship record: ToC, the crown, seasons, best teams,
// the meme tournaments, and the ToC preshow's draft pages), 👥 Players (people and
// their records), 🎖️ Quests (the hub + every quest's board), 🎮 Game (the game
// reference; a MENU-ONLY parent, its pages are nav:false activeAs:'game') and
// 💬 Community (the chat-mined pages, the Daily Chocobo first).
const CHAMPION_TABS = [
  { label: '🏆 Tournament of Champions', desc: 'The reigning-champion leaderboard by title-defense streak, with a seeded bracket and sim odds', href: '?champions' },
  { label: '👑 Line of Succession', desc: 'The complete crown lineage — every reign in order, who dethroned whom', href: '?crown' },
  { label: '📅 Seasons', desc: 'Season by season: title boards, longest reigns, rookies, records — and every unit rolled, rarest first', href: '?seasons' },
  { label: '🏅 Best & Worst Teams', desc: 'The highest- and lowest-graded bracket teams ever assembled — all-time & modern', href: '?teams' },
  { label: '🃏 Meme Tournaments', desc: 'FFTBG’s non-standard events — meme rulesets, Algus and past ToCs — kept here and out of every stat', href: '?memes' },
  { label: '🏈 Preshow Draft', desc: 'The ToC preshow draft room — on the clock, the live board and every team’s picks', href: '?draft' },
  { label: '📮 Draft Pool', desc: 'Every unit put forward for the ToC preshow draft — browse, filter, and submit your own', href: '?submissions' },
];

const CHAT_TABS = [
  { label: '📰 The Daily Chocobo', desc: 'Yesterday in chat, as a tabloid: the big moments, the quotes, the numbers', href: '?chocobo' },
  { label: '💬 Chat Activity', desc: 'Daily chat volume over six years + the most-active-chatter boards', href: '?chat' },
  { label: '🕸️ Social Graph', desc: 'Who @mentions whom — friend clusters & the biggest feuds', href: '?social' },
  { label: '🎁 Gifts', desc: 'Who has gifted the most skills — confirmed by the bot, not by who typed !gift', href: '?gifts' },
  { label: '🎲 What Chat Forces', desc: "Most-forced classes & skills vs win rate", href: '?forced' },
  { label: '📖 Lexicon & copypasta', desc: 'Chat’s in-jokes: emote & phrase lifecycles, the long-form copypasta, the Holy Carpet Bible and the Technospeak wheel', href: '?lexicon' },
];

// 🎖️ Quests (2026-10-23): the hub, then each quest's board (built from the hub's catalog, so they can't drift).
const QUEST_TABS = [
  { label: '🎖️ All quests', desc: 'Every quest at once, with your progress on each and this week’s finishers', href: '?quests' },
  ...QUEST_CATALOG.map((q) => ({ label: q.emoji + ' ' + q.name, desc: q.blurb, href: q.board })),
];

const PLAYER_TABS = [
  { label: 'Directory', desc: 'Search 16k players — win rate, mains, championships', href: '?players' },
  { label: '🏛️ Hall of Fame', desc: 'The 350+ Club — superlatives, records & the elite roster', href: '?hall' },
  { label: '⚔️ Compare', desc: 'Side-by-side career comparison of any two players', href: '?compare' },
  { label: '📊 Records', desc: 'The boards (career, dynasty, attendance, speedrun), the longest title streaks, and the longest class droughts', href: '?players&tab=boards' },
  { label: '💰 Betting', desc: 'Gambler profiles — ROI, win rate, balance over time', href: '?players&tab=betting' },
];

// The 🎮 Game tab groups the game-reference pages. Unlike the other mega-menus it's
// MENU-ONLY — there's no "Game" page, so the parent links to its first child (Maps);
// the grouped sections are nav:false (activeAs:'game') in routes.js and get injected
// here. (Keeps the top bar to ~7 tabs.)
const GAME_TABS = [
  { label: '🎭 Classes', desc: 'Every job & monster (stats, kit, win rates, a live card), the roll guide, and each class’s title leaderboard', href: '?classes' },
  { label: '✨ Abilities', desc: 'Skill glossary — every action skill, support, reaction & move, with output & win rates', href: '?abilities' },
  { label: '🎒 Armory', desc: 'Every item: full stats, a swing-damage calc and usage — plus the combo lookup (has this pair ever rolled?)', href: '?armory' },
  { label: '🗺️ Maps', desc: 'All 138 battle maps — class win-rate edges, 3D terrain & a height calculator', href: '?maps' },
  { label: '📊 Stats', desc: 'Win-rate analytics by class, skill, gear & Brave/Faith, with Meta-Tides trends', href: '?stats' },
  { label: '📚 Learn', desc: 'New here? The guide and every chat command, how the battle engine and its AI work, and the questions chat asks most', href: '?guide' },
  { label: '🎞️ Sprites', desc: 'Every battle sprite off the disc — recolored walk cycles, with GIF export', href: '?gallery' },
];

// The mega-menus stay here (SiteNav presentation detail); the top-level tab list
// + active state are derived from the shared section registry (src/routes.js), so
// adding a page is one entry there — no edit in this file.
const CHILDREN = { champions: CHAMPION_TABS, players: PLAYER_TABS, quests: QUEST_TABS, chat: CHAT_TABS, game: GAME_TABS };
const GAME_TAB = { key: 'game', emoji: '🎮', label: 'Game', href: '?classes',
  desc: 'The game reference — classes, abilities, items, maps, win-rate stats and sprites, plus the guide, mechanics and FAQ.', children: GAME_TABS };
const TABS = [];
for (const s of SECTIONS.filter((x) => x.nav)) {
  if (s.key === 'build') TABS.push(GAME_TAB);   // 🎮 Game sits just before Builder
  TABS.push({ key: s.key, emoji: s.emoji, label: s.label, href: s.href || ('?' + s.param), desc: s.desc, children: CHILDREN[s.key] });
}

export default {
  name: 'SiteNav',
  props: { active: { type: String, default: null } },
  data() { return { tabs: TABS, menuOpen: false }; },
  computed: {
    activeKey() {
      if (this.active) return this.active;          // App / Luxstadio pass it explicitly
      const s = resolveSection();
      return s.activeAs === undefined ? s.key : s.activeAs;   // null = no tab claims active (e.g. changelog)
    },
  },
  methods: {
    // Highlight the active child in any mega-menu (Players + Champions). Standalone
    // pages match by a bare param (?hall/?compare/?crown/?champions); ?players children
    // match on ?tab= (directory = default). Menu-agnostic: only one child matches a URL.
    childActive(href) {
      const cur = new URLSearchParams(window.location.search);
      const h = new URLSearchParams((href.split('?')[1] || ''));
      if (h.has('seasons')) return cur.has('seasons') || cur.has('units');   // Seasons + Units rolled are one entry
      if (h.has('guide')) return ['guide', 'mechanics', 'ai', 'faq'].some((k) => cur.has(k));   // 📚 Learn = guide + mechanics + the AI + FAQ
      if (h.has('lexicon')) return ['lexicon', 'copypasta', 'carpet', 'sekhretpoop'].some((k) => cur.has(k));   // the four chat-lore pages are one entry
      for (const k of ['hall', 'compare', 'crown', 'seasons', 'units', 'vip', 'draft', 'submissions', 'teams', 'champions', 'chat', 'social', 'lexicon', 'copypasta', 'carpet', 'sekhretpoop', 'forced', 'gifts', 'faq', 'chocobo', 'memes', 'maps', 'stats', 'mechanics', 'gallery', 'gear', 'armory', 'abilities', 'classes']) if (h.has(k)) return cur.has(k);
      if (h.has('quests')) return cur.has('quests') && (cur.get('quest') || '') === (h.get('quest') || '');
      if (h.has('players')) {
        const tab = (t) => (['streaks', 'droughts'].includes(t) ? 'boards' : t || 'directory');   // 📊 Records = boards + streaks + droughts
        return cur.has('players') && tab(cur.get('tab')) === tab(h.get('tab'));
      }
      return false;
    },
  },
};
</script>

<style scoped>
/* The 2026-10 refresh's look (assets/css/theme.scss): a navy bar with a brass rule, condensed caps, a brass underline on the active tab. */
.snav { position: sticky; top: 0; z-index: 400; background: linear-gradient(180deg, var(--panel-top), var(--navy2)); border-bottom: 1px solid var(--brass-edge); box-shadow: 0 1px 0 rgba(232, 207, 140, 0.18), 0 3px 12px rgba(0, 0, 0, 0.5); }
.snav-inner { position: relative; display: flex; align-items: stretch; justify-content: space-between; gap: 10px; max-width: 1440px; margin: 0 auto; padding: 0 10px; }
.snav-tabs { display: flex; align-items: stretch; gap: 2px; overflow: visible; }
.snav-item { position: relative; display: flex; align-items: stretch; }
.snav-tab { display: flex; align-items: center; gap: 6px; padding: 8px 11px; color: var(--ink); text-decoration: none; font-family: var(--cond); font-size: 16px; letter-spacing: 0.6px; text-transform: uppercase; white-space: nowrap; border-bottom: 3px solid transparent; transition: background 0.12s ease, color 0.12s ease; }
.snav-tab:hover { background: rgba(217, 180, 94, 0.1); color: var(--brass-hi); }
.snav-tab.active { color: var(--brass-hi); border-bottom-color: var(--brass); background: rgba(217, 180, 94, 0.08); }
.snav-emoji { font-size: 16px; line-height: 1; }
.snav-label { font-weight: 600; }
.snav-caret { font-size: 9px; opacity: 0.6; margin-left: 1px; }
.snav-actions { display: flex; align-items: center; gap: 6px; flex: 0 0 auto; }

/* Players mega-menu (desktop: hover / focus) */
.snav-mega { display: none; position: absolute; top: 100%; left: 0; width: 540px; max-width: 92vw; background: linear-gradient(180deg, var(--panel-top), var(--panel-bottom)); border: 1px solid var(--brass-edge); border-top: none; border-radius: 0 0 6px 6px; box-shadow: 0 12px 28px rgba(0, 0, 0, 0.5); z-index: 401; padding: 12px 14px 14px; }
.snav-item.has-children:hover .snav-mega,
.snav-item.has-children:focus-within .snav-mega { display: block; }
/* Chat is the rightmost tab — open its menu leftward so it doesn't run off-screen. */
.snav-item-chat .snav-mega { left: auto; right: 0; }
.snav-mega-head { display: flex; align-items: baseline; gap: 10px; padding: 2px 4px 10px; border-bottom: 1px solid var(--line); margin-bottom: 8px; flex-wrap: wrap; }
.snav-mega-title { font-family: var(--display); font-weight: 700; font-size: 17px; color: var(--brass-hi); }
.snav-mega-sub { font-size: 12px; color: var(--mute); }
.snav-mega-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 4px; }
.snav-sub { display: block; padding: 8px 10px; border-radius: 3px; text-decoration: none; border-left: 3px solid transparent; }
.snav-sub:hover { background: rgba(217, 180, 94, 0.1); }
.snav-sub.active { border-left-color: var(--brass); background: rgba(217, 180, 94, 0.08); }
.snav-sub-label { display: block; font-size: 13.5px; font-weight: 600; color: var(--ink-hi); }
.snav-sub-desc { display: block; font-size: 11px; color: var(--mute); line-height: 1.35; margin-top: 1px; }

/* Verbose hover tooltip for the non-Players tabs (matches the site .toolTip look, dropped below the bar) */
.snav-tip { display: none; position: absolute; top: calc(100% + 6px); left: 50%; transform: translateX(-50%); width: max-content; max-width: 260px; background: var(--navy); color: var(--ink); border: 1px solid var(--brass-edge); border-radius: 4px; padding: 8px 10px; font-size: 12px; line-height: 1.4; text-align: left; white-space: normal; box-shadow: 0 6px 16px rgba(0, 0, 0, 0.5); z-index: 401; pointer-events: none; }
.snav-item:hover > .snav-tip { display: block; }

/* Narrow desktop, before the ☰ menu takes over at 860px: tighter tabs, then words only. */
@media (max-width: 1100px) and (min-width: 861px) {
  .snav-tab { padding: 8px 7px; letter-spacing: 0.3px; }
  .snav-caret { display: none; }
}
@media (max-width: 980px) and (min-width: 861px) {
  .snav-emoji { display: none; }
}

/* Hamburger (mobile only) */
.snav-burger { display: none; width: 42px; height: 38px; background: none; border: none; color: var(--brass); font-size: 20px; cursor: pointer; padding: 0; }
.snav-burger:hover { color: var(--brass-hi); }

@media (max-width: 860px) {
  .snav-burger { display: flex; align-items: center; justify-content: center; }
  .snav-tabs { display: none; position: absolute; top: 100%; left: 0; right: 0; flex-direction: column; gap: 0; background: var(--navy2); border-bottom: 1px solid var(--brass-edge); box-shadow: 0 10px 22px rgba(0, 0, 0, 0.5); max-height: 82vh; overflow-y: auto; }
  .snav-tabs.open { display: flex; }
  .snav-item { display: block; }
  .snav-tab { border-bottom: 1px solid var(--line); border-left: 3px solid transparent; }
  .snav-tab.active { border-bottom-color: var(--line); border-left-color: var(--brass); }
  .snav-caret { display: none; }
  .snav-tip { display: none !important; }   /* no hover on touch */
  /* mega-menu becomes an inline single-column list inside the drawer */
  .snav-mega { display: block; position: static; width: auto; max-width: none; border: none; border-radius: 0; box-shadow: none; background: rgba(0, 0, 0, 0.22); padding: 0; }
  .snav-mega-head { display: none; }
  .snav-mega-grid { grid-template-columns: 1fr; gap: 0; }
  .snav-sub { border-radius: 0; padding: 8px 10px 8px 40px; border-bottom: 1px solid var(--line); }
  .snav-sub-desc { display: none; }
}
</style>
